import React, { useEffect, useState } from "react";
import "../../styles/DeliveryHealth.css";
import ActiveDeliveryDelayIcon from "../../icons/ActiveDeliveryDelayIcon";
import PreparationDelayIcon from "../../icons/PreparationDelayIcon";
import deliveryHealthController from "../../controllers/deliveryHealthController";
import { userService } from "../../services/userService";
import { translateText, translateLabel, useTranslationSnapshot } from "./../../translations/runtime";


const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getPayload = (response) => {
  const payload = response?.data;
  return payload?.data ?? payload?.result ?? payload ?? {};
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const formatMinutes = (value) => `${Number(value || 0).toFixed(1)} min`;

const DeliveryStatIcon = ({ type }) => { useTranslationSnapshot(); return 
  type === "alert" ? <PreparationDelayIcon /> : <ActiveDeliveryDelayIcon />; };

function DeliveryHealth() {
  useTranslationSnapshot();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchOverview = async () => {
      setLoading(true);
      setError("");

      try {
        const response =
          await deliveryHealthController.getDeliveryHealthOverview({
            jwt: getStoredToken(),
            page: 1,
            size: 10,
          });

        if (
          response instanceof Error ||
          response?.isAxiosError ||
          response?.response
        ) {
          throw response;
        }

        if (isCurrentRequest) setOverview(getPayload(response));
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setOverview(null);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load delivery health.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchOverview();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const dashboard = overview?.dashboard || {};
  const delayCards = [
    {
      label: "Preparation Delays Flagged",
      value: formatCount(dashboard.preparationDelaysFlagged),
      tone: "warning",
      icon: "alert",
    },
    {
      label: "Active Delivery Delays",
      value: formatCount(dashboard.activeDeliveryDelays),
      tone: "neutral",
      icon: "clock",
    },
  ];
  const regionalRows = Array.isArray(overview?.rows) ? overview.rows : [];

  return (
    <div className="delivery-health-wrapper">
      <div className="delivery-health-title-container">
        <div className="delivery-health-title">Delivery Health Overview</div>
        <div className="delivery-health-subtitle">
          Delivery conditions affecting ETA, vendor ranking, and discovery
          visibility
        </div>
      </div>

      {loading && (
        <div className="delivery-health-state">Loading delivery health...</div>
      )}
      {!loading && error && (
        <div className="delivery-health-state delivery-health-error">
          {translateText(error)}
        </div>
      )}

      {!loading && !error && (
        <div className="delivery-health-stat-row">
          {delayCards.map((card) => (
            <div
              className={`delivery-health-stat-card delivery-health-stat-card-${card.tone}`}
              key={card.label}
            >
              <DeliveryStatIcon type={card.icon} />
              <div className="delivery-health-stat-content">
                <div className="delivery-health-stat-label">{translateText(card.label)}</div>
                <div className="delivery-health-stat-value">{card.value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && (
        <div className="delivery-health-table-card">
          <div className="delivery-health-table-title">
            Regional Capacity &amp; Performance
          </div>
          <div className="delivery-health-table-subtitle">
            Real-time metrics per operating region
          </div>

          <div className="delivery-health-table">
            <div className="delivery-health-table-header">
              <div>{translateLabel("txt_admin_region", "Region")}</div>
              <div>{translateLabel("txt_admin_active_drivers", "Active Drivers")}</div>
              <div>{translateLabel("txt_admin_acceptance_rate", "Acceptance Rate")}</div>
              <div>{translateLabel("txt_admin_avg_pickup_time", "Avg Pickup Time")}</div>
              <div>{translateLabel("txt_admin_on_time", "On - Time %")}</div>
            </div>

            {regionalRows.length === 0 && (
              <div className="delivery-health-table-row">
                <div className="delivery-health-state">No delivery data.</div>
                <div />
                <div />
                <div />
                <div />
              </div>
            )}

            {regionalRows.map((row) => (
              <div className="delivery-health-table-row" key={row.region}>
                <div>{row.region}</div>
                <div>{formatCount(row.activeDrivers)}</div>
                <div
                  className={
                    Number(row.acceptanceRate || 0) < 85
                      ? "delivery-health-danger-text"
                      : ""
                  }
                >
                  {formatPercent(row.acceptanceRate)}
                </div>
                <div>{formatMinutes(row.avgPickupTime)}</div>
                <div>
                  <span className="delivery-health-on-time-badge">
                    {formatPercent(row.onTimePercentage)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryHealth;
